import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { CURRENT_SESSION_VERSION } from '../config/sessionConfig';
import { invalidateBookingMedia } from '../services/imageService';

// יצירת קונטקסט אימות
const AuthContext = createContext();

// פרובידר לאימות
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // בדיקה האם המשתמש מחובר (על פי טוקן ב-localStorage)
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        // הגדרת הטוקן בכותרות הבקשה
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        try {
          // ניסיון לפענח את הטוקן
          const decoded = jwtDecode(token);

          // בדיקה אם הטוקן פג תוקף
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            // אם הטוקן פג תוקף, מנקים את ה-localStorage
            invalidateBookingMedia();
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setIsAuthenticated(false);
            setUser(null);
          } else {
            // בדיקת גרסת סשן גלובלית - מנתק טוקנים ישנים
            if (!decoded.sessionVersion || decoded.sessionVersion !== CURRENT_SESSION_VERSION) {
              invalidateBookingMedia();
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              setIsAuthenticated(false);
              setUser(null);
            } else {
              // אם הטוקן תקף, מעדכנים את המצב
              setIsAuthenticated(true);
              setUser(decoded);
            }
          }
        } catch (error) {
          invalidateBookingMedia();
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        invalidateBookingMedia();
        delete axios.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setUser(null);
      }

      setLoading(false);
    };

    checkAuth();
    const onStorage = event => {
      if (event.key === 'token' || event.key === null) {
        invalidateBookingMedia();
        setIsAuthenticated(false);
        setUser(null);
        checkAuth();
      }
    };
    const onPageHide = () => invalidateBookingMedia();
    const onPageShow = event => {
      if (event.persisted) checkAuth();
    };
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('storage', onStorage);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pagehide', onPageHide);
      invalidateBookingMedia();
    };
  }, []);

  // התחברות
  const login = async (credentials) => {
    try {
      // שליחת בקשת התחברות לשרת
      const loginUrl = '/api/auth/login';

      const response = await axios.post(loginUrl, credentials);

      // שמירת הטוקן בלוקל סטוראג'
      const { token, user } = response.data;
      invalidateBookingMedia();
      localStorage.setItem('token', token);

      // הגדרת הטוקן בכותרות הבקשה לבקשות עתידיות
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // עדכון מצב האימות
      setIsAuthenticated(true);
      setUser(user);

      return { success: true };
    } catch (error) {

      let errorMessage = 'אירעה שגיאה בהתחברות';

      if (error.response) {
        // במקרה של תשובת שגיאה מהשרת
        errorMessage = error.response.status === 401 ? 'שם משתמש או סיסמה שגויים' : errorMessage;
      } else if (error.request) {
        // במקרה שהבקשה נשלחה אבל לא התקבלה תשובה
        errorMessage = 'לא התקבלה תשובה מהשרת, בדוק את החיבור לאינטרנט';
      } else {
        // במקרה של שגיאה אחרת
        errorMessage = 'שגיאה בהגדרת הבקשה';
      }

      return { success: false, error: errorMessage };
    }
  };

  // התנתקות
  const logout = () => {
    // הסרת הטוקן מה-localStorage
    invalidateBookingMedia();
    localStorage.removeItem('token');

    // הסרת הטוקן מכותרות הבקשה
    delete axios.defaults.headers.common['Authorization'];

    // עדכון מצב האימות
    setIsAuthenticated(false);
    setUser(null);

  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// הוק שימושי לגישה לקונטקסט האימות
export const useAuth = () => {
  return useContext(AuthContext);
};
